import React, { useState, useEffect } from 'react';
import {
  Settings, Check, X, Save, Shield, Users, Crown,
  ShoppingBag, Smartphone, Shirt, ShoppingCart, Gem,
  Sofa, Sparkles, Dumbbell, Store, AlertTriangle
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const TEMPLATE_ICONS = {
  ecommerce_starter: ShoppingBag,
  portfolio_minimal: Users,
  business_professional: Shield,
  service_provider: Settings,
  landing_page: Crown,
  flipkart_marketplace: ShoppingBag,
  amazon_marketplace: Crown,
  electronics_store: Smartphone,
  fashion_store: Shirt,
  grocery_store: ShoppingCart,
  jewelry_store: Gem,
  furniture_store: Sofa,
  beauty_store: Sparkles,
  sports_store: Dumbbell,
  retail_store: Store
};

const TemplatePermissions = () => {
  const [permissions, setPermissions] = useState(null);
  const [allTemplates, setAllTemplates] = useState([]);
  const [templateDetails, setTemplateDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [memberAllowed, setMemberAllowed] = useState([]);

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const response = await api.get('/admin/shops/template-permissions');
      setPermissions(response.data.permissions);
      setAllTemplates(response.data.all_templates || []);
      setTemplateDetails(response.data.template_details || {});
      setMemberAllowed(response.data.permissions?.member_allowed_templates || 
                       response.data.permissions?.default_member_access || []);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      toast.error('Failed to load template permissions');
    } finally {
      setLoading(false);
    }
  };

  const toggleTemplate = (templateKey) => {
    setMemberAllowed(prev => {
      if (prev.includes(templateKey)) {
        return prev.filter(k => k !== templateKey);
      } else {
        return [...prev, templateKey];
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/shops/template-permissions', {
        ...permissions,
        member_allowed_templates: memberAllowed
      });
      toast.success('Template permissions updated successfully');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const enableAllFlipkart = () => {
    const flipkartTemplates = allTemplates.filter(k => templateDetails[k]?.is_flipkart_style);
    setMemberAllowed(prev => [...new Set([...prev, ...flipkartTemplates])]);
  };

  const disableAllFlipkart = () => {
    const flipkartTemplates = allTemplates.filter(k => templateDetails[k]?.is_flipkart_style);
    setMemberAllowed(prev => prev.filter(k => !flipkartTemplates.includes(k)));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Separate basic and Flipkart templates
  const basicTemplates = allTemplates.filter(k => !templateDetails[k]?.is_flipkart_style);
  const flipkartTemplates = allTemplates.filter(k => templateDetails[k]?.is_flipkart_style);

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <Shield className="w-7 h-7 text-primary" />
              Template Permissions
            </h1>
            <p className="text-muted-foreground mt-1">
              Control which shop templates members can access
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-semibold flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            Save Changes
          </button>
        </div>

        {/* Quick Actions */}
        <div className="glass-card p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="w-4 h-4" />
              <span>Members currently have access to {memberAllowed.length} templates</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={enableAllFlipkart}
                className="px-4 py-2 bg-green-500/20 text-green-500 rounded-lg text-sm font-medium hover:bg-green-500/30"
              >
                Enable All Flipkart Templates
              </button>
              <button
                onClick={disableAllFlipkart}
                className="px-4 py-2 bg-red-500/20 text-red-500 rounded-lg text-sm font-medium hover:bg-red-500/30"
              >
                Disable All Flipkart Templates
              </button>
            </div>
          </div>
        </div>

        {/* Basic Templates */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Store className="w-5 h-5 text-blue-500" />
            Basic Templates
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {basicTemplates.map(templateKey => {
              const details = templateDetails[templateKey] || {};
              const Icon = TEMPLATE_ICONS[templateKey] || Store;
              const isAllowed = memberAllowed.includes(templateKey);

              return (
                <div
                  key={templateKey}
                  onClick={() => toggleTemplate(templateKey)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    isAllowed 
                      ? 'border-green-500 bg-green-500/10' 
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isAllowed ? 'bg-green-500/20' : 'bg-white/10'
                      }`}>
                        <Icon className={`w-5 h-5 ${isAllowed ? 'text-green-500' : 'text-muted-foreground'}`} />
                      </div>
                      <div>
                        <p className="font-medium">{details.name || templateKey}</p>
                        <p className="text-xs text-muted-foreground">{details.type}</p>
                      </div>
                    </div>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      isAllowed ? 'bg-green-500' : 'bg-white/10'
                    }`}>
                      {isAllowed ? (
                        <Check className="w-4 h-4 text-white" />
                      ) : (
                        <X className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Flipkart Templates */}
        <div className="glass-card p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Crown className="w-5 h-5 text-yellow-500" />
            Flipkart-Style Premium Templates
            <span className="px-2 py-1 bg-yellow-500/20 text-yellow-500 text-xs rounded-full">
              {flipkartTemplates.length} templates
            </span>
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            These templates include mega menus, emergency offers, flash sales, and video product displays.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {flipkartTemplates.map(templateKey => {
              const details = templateDetails[templateKey] || {};
              const Icon = TEMPLATE_ICONS[templateKey] || Store;
              const isAllowed = memberAllowed.includes(templateKey);

              return (
                <div
                  key={templateKey}
                  onClick={() => toggleTemplate(templateKey)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    isAllowed 
                      ? 'border-yellow-500 bg-yellow-500/10' 
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isAllowed ? 'bg-yellow-500/20' : 'bg-white/10'
                      }`}>
                        <Icon className={`w-5 h-5 ${isAllowed ? 'text-yellow-500' : 'text-muted-foreground'}`} />
                      </div>
                      <div>
                        <p className="font-medium">{details.name || templateKey}</p>
                        <p className="text-xs text-muted-foreground">{details.type}</p>
                      </div>
                    </div>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      isAllowed ? 'bg-yellow-500' : 'bg-white/10'
                    }`}>
                      {isAllowed ? (
                        <Check className="w-4 h-4 text-white" />
                      ) : (
                        <X className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="mt-6 flex items-center justify-center gap-8 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500" />
            <span>Allowed for Members</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-white/10" />
            <span>Admin Only</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePermissions;
